/**
 * Leaderboard body (plan 23 row 40, lane BM): two tome tabs — Top characters
 * (rank, name, calling, level, Twitch) and Online now (name, calling, level,
 * zone). Presentational: LeaderboardWindow.tsx fetches, the gallery and
 * tests render it from fixtures. A ledger on the Tome window's parchment
 * (Tome UI Round 5, E4): tabs stay put while the table scrolls under a
 * sticky header, and your own row (`selfName`) is marked on both lists.
 */
import React from 'react';
import type { LeaderboardEntry, OnlinePlayerEntry } from '../../../../../shared/social/socialWire';
import { classColor, classIcon, formatCallingName } from '../../../theme';
import { TomeChip } from '../../../primitives/tome';

export type LeaderboardTab = 'top' | 'online';

export interface LeaderboardViewProps {
  tab: LeaderboardTab;
  onTabChange: (tab: LeaderboardTab) => void;
  top: LeaderboardEntry[] | null;
  online: { total: number; players: OnlinePlayerEntry[] } | null;
  error: string | null;
  onRefresh: () => void;
  /** Your character's name: that row is marked "You" on both lists. */
  selfName?: string | null;
}

const Calling: React.FC<{ calling: string }> = ({ calling }) => (
  <span className="leaderboard-calling" style={{ '--calling': classColor(calling) } as React.CSSProperties}>
    <span aria-hidden="true">{classIcon(calling)}</span> {formatCallingName(calling)}
  </span>
);

const Name: React.FC<{ name: string; self: boolean }> = ({ name, self }) => (
  <td className="leaderboard-name">
    <span>{name}</span>
    {self && <TomeChip tone="active" cut="clean" className="leaderboard-you">You</TomeChip>}
  </td>
);

const TABS: ReadonlyArray<[LeaderboardTab, string]> = [['top', 'Top characters'], ['online', 'Online now']];

export const LeaderboardView: React.FC<LeaderboardViewProps> = ({ tab, onTabChange, top, online, error, onRefresh, selfName }) => {
  const isSelf = (name: string) => !!selfName && name === selfName;
  return <>
    <div className="leaderboard-tabs" role="tablist" aria-label="Leaderboard">
      {TABS.map(([id, label]) => (
        <button key={id} type="button" role="tab" id={`leaderboard-tab-${id}`} aria-selected={tab === id} className="leaderboard-tab"
          data-active={tab === id ? 'true' : 'false'} onClick={() => onTabChange(id)}>
          {label}{id === 'online' && online ? ` · ${online.total}` : ''}
        </button>
      ))}
      <button type="button" className="leaderboard-refresh" onClick={onRefresh} aria-label="Refresh">↻</button>
    </div>
    <div className="leaderboard-ledger-page" role="tabpanel" aria-labelledby={`leaderboard-tab-${tab}`}>
      {error && <p className="social-tome-empty" role="status">{error}</p>}
      {!error && tab === 'top' && (top === null
        ? <p className="social-tome-empty" role="status">Reading the rolls…</p>
        : top.length === 0
          ? <p className="social-tome-empty">No characters on the board yet.</p>
          : <table className="leaderboard-table" data-testid="leaderboard-top">
            <thead><tr><th>#</th><th>Name</th><th>Calling</th><th className="leaderboard-num">Level</th><th>Twitch</th></tr></thead>
            <tbody>
              {top.map(entry => (
                <tr key={`${entry.rank}-${entry.name}`} data-rank={entry.rank} data-self={isSelf(entry.name) || undefined}>
                  <td className="leaderboard-rank">{entry.rank}</td>
                  <Name name={entry.name} self={isSelf(entry.name)} />
                  <td><Calling calling={entry.calling} /></td>
                  <td className="leaderboard-num">{entry.level}</td>
                  <td className="leaderboard-twitch">
                    {entry.twitch ? (
                      <span className="leaderboard-twitch-name" title={`Twitch: ${entry.twitch}`}>
                        {entry.twitch}
                      </span>
                    ) : (
                      <span className="leaderboard-unlinked" title="No linked Twitch account">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>)}
      {!error && tab === 'online' && (online === null
        ? <p className="social-tome-empty" role="status">Counting heads…</p>
        : online.players.length === 0
          ? <p className="social-tome-empty">Nobody else is out in the world right now.</p>
          : <table className="leaderboard-table" data-testid="leaderboard-online">
            <thead><tr><th>Name</th><th>Calling</th><th className="leaderboard-num">Level</th><th>Zone</th></tr></thead>
            <tbody>
              {online.players.map((player, index) => (
                <tr key={`${player.name}-${index}`} data-self={isSelf(player.name) || undefined}>
                  <Name name={player.name} self={isSelf(player.name)} />
                  <td><Calling calling={player.calling} /></td>
                  <td className="leaderboard-num">{player.level}</td>
                  <td className="leaderboard-zone">{player.zone}</td>
                </tr>
              ))}
            </tbody>
          </table>)}
    </div>
  </>;
};
